import * as React from "react";
import type { MediaFile } from "./patient-media.types.ts";

interface PatientMediaContextValue {
  // Viewer registration
  registerViewer: (id: string) => void;
  unregisterViewer: (id: string) => void;
  getViewerFile: (id: string) => MediaFile | null;
  // File selection — cycles through registered viewers
  selectFile: (file: MediaFile) => void;
}

const PatientMediaCtx = React.createContext<PatientMediaContextValue | null>(
  null,
);

export function PatientMediaProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Use a ref for the ordered list (no re-render needed on register/unregister)
  const viewerIdsRef = React.useRef<string[]>([]);
  const nextSlotRef = React.useRef(0);
  const [viewerFiles, setViewerFiles] = React.useState<
    Record<string, MediaFile | null>
  >({});

  const registerViewer = React.useCallback((id: string) => {
    viewerIdsRef.current = [...viewerIdsRef.current, id];
    setViewerFiles((prev) => ({ ...prev, [id]: null }));
  }, []);

  const unregisterViewer = React.useCallback((id: string) => {
    viewerIdsRef.current = viewerIdsRef.current.filter((v) => v !== id);
    setViewerFiles((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const selectFile = React.useCallback((file: MediaFile) => {
    const ids = viewerIdsRef.current;
    if (ids.length === 0) return;
    const slotId = ids[nextSlotRef.current % ids.length]!;
    nextSlotRef.current++;
    setViewerFiles((prev) => ({ ...prev, [slotId]: file }));
  }, []);

  const getViewerFile = React.useCallback(
    (id: string) => viewerFiles[id] ?? null,
    [viewerFiles],
  );

  return (
    <PatientMediaCtx.Provider
      value={{ registerViewer, unregisterViewer, getViewerFile, selectFile }}
    >
      {children}
    </PatientMediaCtx.Provider>
  );
}

export function usePatientMedia(): PatientMediaContextValue {
  const ctx = React.useContext(PatientMediaCtx);
  if (!ctx)
    throw new Error("usePatientMedia must be used within PatientMediaProvider");
  return ctx;
}
